import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Modal, Table, message } from 'antd';
import type { TableColumnsType } from 'antd';
import Breadcrumbs from '../../../components/Breadcrumbs';
import { usePlatformState } from '../../../features/platform/platformStateSlice';
import {
  useGetImportStatusQuery,
  useImportSecMutation,
  useGetEciDetailQuery,
  useLazyGetSecTemplateQuery,
} from '../../../features/platform/platformApi';

/* ------------------------------------------------------------------ *
 * Platform Admin — Import standard sources (onboarding Step 1).
 *   ECI — synced from the website; click to drill into PCs / ACs / booths.
 *   SEC — upload the PRI/ULB Excel (+ download a blank template).
 *   LGD — synced from lgdirectory.gov.in (downloader coming soon).
 * Status is auto-detected from the real data.
 * ------------------------------------------------------------------ */

type SourceKey = 'LGD' | 'ECI' | 'SEC';

const SOURCES: { key: SourceKey; title: string; desc: string }[] = [
  { key: 'LGD', title: 'LGD', desc: 'Local Government Directory — districts, sub-districts, villages, blocks, gram panchayats, urban bodies and wards.' },
  { key: 'ECI', title: 'ECI', desc: 'Election Commission of India — parliamentary & assembly constituencies and polling booths.' },
  { key: 'SEC', title: 'SEC', desc: 'State Election Commission — local bodies and their wards.' },
];

const STATUS_STYLE: Record<string, string> = {
  notstarted: 'bg-[#f3f4f6] text-[#6b7280]',
  running: 'bg-[#fffbeb] text-[#b45309]',
  done: 'bg-brand-shade text-brand',
  error: 'bg-[#fee2e2] text-[#dc2626]',
};
const STATUS_LABEL: Record<string, string> = {
  notstarted: 'Not started', running: 'In progress', done: 'Done', error: 'Error',
};

export default function ImportStandardSourcesPage() {
  const navigate = useNavigate();
  const { code, name } = usePlatformState();
  const { data: statuses = [] } = useGetImportStatusQuery(code);
  const [importSec, { isLoading: secBusy }] = useImportSecMutation();
  const [fetchTemplate, { isFetching: tplBusy }] = useLazyGetSecTemplateQuery();

  const [eciOpen, setEciOpen] = useState(false);
  const { data: eci } = useGetEciDetailQuery(code, { skip: !eciOpen });

  const secInput = useRef<HTMLInputElement>(null);
  const statusOf = (key: SourceKey) => statuses.find((s) => s.source === key);

  const uploadSec = async (file: File) => {
    try {
      await importSec({ code, file }).unwrap();
      message.success('SEC file uploaded.');
    } catch {
      message.error('SEC import failed.');
    }
  };

  const downloadSec = async (filled: boolean) => {
    try {
      const blob = (await fetchTemplate(filled ? { filled: true } : undefined).unwrap()) as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filled ? 'SEC_PRI_ULB_data.xlsx' : 'SEC_PRI_ULB_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      message.error('Could not download.');
    }
  };

  const eciCols: TableColumnsType<NonNullable<typeof eci>['assemblies'][number]> = [
    { title: 'AC', dataIndex: 'ac_no', width: 70 },
    { title: 'Assembly', dataIndex: 'name' },
    { title: 'Parliament (PC)', dataIndex: 'pc' },
    { title: 'Booths', dataIndex: 'booths', width: 90 },
  ];

  const card = (src: { key: SourceKey; title: string; desc: string }) => {
    const status = statusOf(src.key);
    const st = status?.status ?? 'notstarted';
    const done = st === 'done';

    return (
      <div key={src.key} className="flex flex-col gap-3 rounded-xl border border-[#e5e7eb] bg-white p-5 shadow-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="flex size-9 items-center justify-center rounded-lg bg-brand-shade text-sm font-bold text-brand">
              {src.title}
            </span>
            <h3 className="text-base font-bold text-ink">{src.title}</h3>
          </div>
          <span className={['rounded-full px-2.5 py-1 text-xs font-medium', STATUS_STYLE[st]].join(' ')}>
            {STATUS_LABEL[st]}
          </span>
        </div>

        <p className="text-xs leading-5 text-ink-secondary">{src.desc}</p>
        {status?.message && <p className="text-[11px] leading-4 text-[#94a3b8]">{status.message}</p>}

        <div className="flex flex-wrap items-center gap-3 pt-1">
          {/* ECI — synced; drill into the structure */}
          {src.key === 'ECI' && (
            <>
              {done && <span className="text-[13px] font-semibold text-brand">✓ Imported</span>}
              <button
                type="button"
                onClick={() => navigate('/admin/eci-pull')}
                className="h-9 rounded-lg bg-brand px-4 text-[13px] font-semibold text-white shadow-btn transition-colors hover:bg-brand-dark"
              >
                {done ? 'Pull / update ECI data' : 'Pull ECI data'}
              </button>
              {done && (
                <button
                  type="button"
                  onClick={() => setEciOpen(true)}
                  className="h-9 rounded-lg border border-brand px-4 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade"
                >
                  View booths / assemblies
                </button>
              )}
            </>
          )}

          {/* SEC — upload + template */}
          {src.key === 'SEC' && (
            <>
              {done && <span className="text-[13px] font-semibold text-brand">✓ Imported</span>}
              <input
                ref={secInput}
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadSec(f);
                  e.target.value = '';
                }}
              />
              <button
                type="button"
                onClick={() => secInput.current?.click()}
                disabled={secBusy}
                className="h-9 rounded-lg border border-brand px-4 text-[13px] font-semibold text-brand transition-colors hover:bg-brand-shade disabled:opacity-60"
              >
                {secBusy ? 'Uploading…' : done ? 'Re-upload Excel' : 'Upload SEC Excel'}
              </button>
              <button
                type="button"
                onClick={() => downloadSec(false)}
                disabled={tplBusy}
                className="text-[12px] font-medium text-brand underline hover:text-brand-dark disabled:opacity-60"
              >
                Download template
              </button>
              {done && (
                <button
                  type="button"
                  onClick={() => downloadSec(true)}
                  disabled={tplBusy}
                  className="text-[12px] font-medium text-brand underline hover:text-brand-dark disabled:opacity-60"
                >
                  Download current data
                </button>
              )}
            </>
          )}

          {/* LGD — synced from the website (downloader pending) */}
          {src.key === 'LGD' && (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                disabled
                className="h-9 cursor-not-allowed rounded-lg border border-[#e5e7eb] bg-[#f9fafb] px-4 text-[13px] font-semibold text-[#9ca3af]"
              >
                Sync from LGD
              </button>
              <span className="text-[11px] text-[#94a3b8]">
                Coming soon — will pull directly from lgdirectory.gov.in.
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        items={[
          { label: 'All states', to: '/admin/overview' },
          { label: 'Onboarding', to: '/admin/onboarding' },
          { label: 'Import standard sources' },
        ]}
      />
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-brand">PLATFORM ADMIN · ONBOARDING STEP 1</span>
        <h1 className="text-2xl font-bold text-ink">Import standard sources</h1>
        <p className="text-sm leading-6 text-ink-secondary">
          Which data are you importing for <span className="font-semibold">{name}</span>? Each source
          loads a different way — ECI is synced from the website, SEC is uploaded, LGD is synced from LGD.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {SOURCES.map(card)}
      </div>

      {/* ECI drill-in */}
      <Modal
        open={eciOpen}
        title={`ECI structure · ${name}`}
        onCancel={() => setEciOpen(false)}
        footer={null}
        width={760}
      >
        <div className="mb-3 flex gap-6 text-sm">
          <span><b>{eci?.parliament ?? '—'}</b> Parliament seats</span>
          <span><b>{eci?.assembly ?? '—'}</b> Assembly seats</span>
          <span><b>{eci?.booths ?? '—'}</b> Booths</span>
        </div>
        <Table
          rowKey="ac_no"
          size="small"
          columns={eciCols}
          dataSource={eci?.assemblies ?? []}
          pagination={{ pageSize: 10, showSizeChanger: false }}
        />
      </Modal>
    </div>
  );
}
